import React, { useState } from 'react';
import { AxiosResponse } from 'axios';

import { useMutation, useQuery } from '@hooks/reactQuery';

import { auth } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setSnackbar } from '@slices/common';

import { uploadFileToGcloud } from '@api/common';
import {
	approveImport,
	getAllFields,
	getFieldSamples,
	getImportStatistic,
	getImportTypes,
	getSampleUrl,
	getSystemFields,
	getUploadURL,
	runAllFields,
	runAssetsStatistics,
	runFieldSamples,
	submitFieldsMapping,
	submitImportRules,
} from '@api/importAssets';

import { checkIsRequestAccepted, extractQueryParams, isEmpty } from '@utils/common';
import { fileSaver } from '@utils/fileSaver';
import { fieldsOptionsFormatted, optionsFormatted, optionsFormattedByName } from '@utils/formatter';
import { handleErrorFromMessage } from '@utils/handleRequestError';
import { generateHeaders } from '@utils/headersMapper';

import { IFieldsMappingDTO, ISaveImportRulesDTO } from '@interfaces/import/dto/importDTO';
import { IFieldsSamples, ISystemFields, IUploadUrlType } from '@interfaces/import/import';

import { ESnackbarTypes } from '@enums/common';
import { EImportingDataType, TImportingDataType } from '@enums/dropzone';
import { ESuccessCodes } from '@enums/successCodes';

import { refetchOptions } from '@constants/common';
import {
	ALL_FIELDS,
	ASSET_SYSTEM_FIELDS,
	DOWNLOAD_ASSET_SAMPLE,
	FIELD_SAMPLES,
	IMPORT_EXECUTE,
	IMPORT_STATISTIC,
	IMPORT_TYPES,
	SAVE_FIELDS_MAPPING,
	SAVE_IMPORT_RULES,
	UPLOAD_URL,
} from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';
import {
	REFETCH_STATISTICS_INTERVAL,
} from '@components/ModalComponents/ImportMapDataContainer/ImportMapData/constants';

interface IUseImportAssetsParams {
	setImportIds: React.Dispatch<React.SetStateAction<string[]>>;
	importId: string | undefined;
	closeModals: () => void;
	importType: TImportingDataType;
	isResultStatisticComponent: boolean;
	isImportRulesComponent: boolean;
	isFieldsMappingComponent: boolean;
}

export const useImportAssets = (props: IUseImportAssetsParams) => {
	const dispatch = useAppDispatch();

	const {
		setImportIds,
		importId,
		closeModals,
		importType,
		isResultStatisticComponent,
		isImportRulesComponent,
		isFieldsMappingComponent,
	} = props;

	const {
		accessPermission: {
			assetOwnerOrganizationAdministrator,
			networkOperatorOrganizationAdministrator,
			assetOwnerAttributeUpdater,
			assetConsumerOrganizationAdministrator,
		},
	} = useAppSelector(auth);

	const [refetchIntervalImportStatistic, setRefetchIntervalImportStatistic]
		= useState<number | false>(REFETCH_STATISTICS_INTERVAL);
	const [refetchIntervalAllFields, setRefetchIntervalAllFields]
		= useState<number | false>(REFETCH_STATISTICS_INTERVAL);
	const [refetchIntervalFieldsSamples, setRefetchIntervalFieldsSamples]
		= useState<number | false>(REFETCH_STATISTICS_INTERVAL);

	const resetIntervals = () => {
		setRefetchIntervalImportStatistic(REFETCH_STATISTICS_INTERVAL);
		setRefetchIntervalAllFields(REFETCH_STATISTICS_INTERVAL);
		setRefetchIntervalFieldsSamples(REFETCH_STATISTICS_INTERVAL);
	};

	const {
		mutate: createAssetUploadUrl,
		data: uploadUrlData,
		isLoading: isLoadingCreateUploadUrl,
	} = useMutation<IUploadUrlType>({
		mutationKey: [UPLOAD_URL],
		mutationFn: ({ files }: { files: Array<File> }) => {
			resetIntervals();

			getUploadURL({ fileName: files[0].name })
				.then((data) => {
					const headers = generateHeaders(data?.headers);
					const url = data?.uploadUrl;

					uploadFileToGcloud({ url, data: files[0], headers })
						.then(() => {
							if (data?.importId) setImportIds([data.importId]);
						})
						.then(() => {
							if (data?.importId) runAllFields(data.importId as string)
								.catch((err) => {
									dispatch(setSnackbar({
										message: err?.message || responseMessages.error,
										type: ESnackbarTypes.error,
									}));

								});
						})
						.catch((err) => {
							dispatch(setSnackbar({
								message: err?.message || responseMessages.error,
								type: ESnackbarTypes.error,
							}));
						});
				}).catch(err => handleErrorFromMessage(err));
		},
	});

	const {
		mutate: downloadSample,
		data: assetSampleUrl,
	} = useMutation({
		mutationKey: [DOWNLOAD_ASSET_SAMPLE],
		mutationFn: () => {
			getSampleUrl()
				.then((data) => {
					if (data?.downloadUrl) fileSaver(data.downloadUrl);
				}).catch(err => {
				dispatch(setSnackbar({
					message: err?.message || responseMessages.error,
					type: ESnackbarTypes.error,
				}));
			});
		},
	});

	const {
		mutate: saveImportRules,
		isLoading: isLoadingAssetImportRules,
	} = useMutation<ISaveImportRulesDTO & { nextClickHandler: () => void }>({
		mutationKey: [SAVE_IMPORT_RULES, importId],
		mutationFn: (data: ISaveImportRulesDTO & { nextClickHandler: () => void }) =>
			submitImportRules(data)
				.then(() => {
					runFieldSamples(importId as string)
						.catch((err) => {
							dispatch(setSnackbar({
								message: err?.message || responseMessages.error,
								type: ESnackbarTypes.error,
							}));
						});
				})
				.then(() => {
					data.nextClickHandler();
				}),
	});

	const {
		data: assetFieldsSamples,
		isLoading: isLoadingFieldsSamples,
		isFetching: isFetchingFieldsSamples,
	} = useQuery<AxiosResponse, {
		data: Array<IFieldsSamples>;
		status: number;
	}>({
		queryKey: [FIELD_SAMPLES, importId],
		queryFn: extractQueryParams(getFieldSamples),
		enabled: [!!importId, isFieldsMappingComponent],
		refetchInterval: refetchIntervalFieldsSamples,
		refetchOnWindowFocus: false,
		refetchOnmount: false,
		select: (response) => {
			const fields = !isEmpty(response.data) ? optionsFormattedByName(response.data) : [];

			return { data: fields as Array<IFieldsSamples>, status: response.status };
		},
		onSuccess: (data) => {
			if (checkIsRequestAccepted(data)) {
				setRefetchIntervalFieldsSamples(false);
			}
		},
	});

	const {
		mutate: saveAssetFieldsMapping,
		isLoading: isLoadingSaveFieldsMapping,
	} = useMutation<{
		fieldsMapping: Array<IFieldsMappingDTO>;
		nextClick: () => void;
	}>({
		mutationKey: [SAVE_FIELDS_MAPPING],
		mutationFn: (data: { fieldsMapping: Array<IFieldsMappingDTO>; nextClick: () => void }) =>
			submitFieldsMapping({ id: importId, fieldsMapping: data.fieldsMapping })
				.then(() => {
					runAssetsStatistics(importId as string).catch(err => handleErrorFromMessage(err));
				})
				.then(() => {
					data.nextClick();
				})
				.catch(err => handleErrorFromMessage(err)),
		...refetchOptions,
		enabled: [!!importId],
	});

	const {
		data: assetImportStatistic,
		isLoading: isLoadingAssetsImportStatistic,
		isFetching: isFetchingAssetsImportStatistic,
	} = useQuery({
		queryKey: [IMPORT_STATISTIC, importId],
		queryFn: extractQueryParams(getImportStatistic),
		refetchInterval: refetchIntervalImportStatistic,
		refetchOnWindowFocus: false,
		refetchOnmount: false,
		onSuccess: (data: AxiosResponse) => {
			if (checkIsRequestAccepted(data)) {
				setRefetchIntervalImportStatistic(false);
			}
		},
		enabled: [!!importId, isResultStatisticComponent],
	});

	const {
		mutate: executeAssetImport,
		isLoading: isLoadingExecuteImport,
	} = useMutation({
		mutationKey: [IMPORT_EXECUTE, importId],
		mutationFn: () => approveImport(importId!),
		onSuccess: () => {
			closeModals();

			dispatch(setSnackbar({
				message: responseMessages.importAssets,
				type: ESnackbarTypes.success,
			}));
		},
		enabled: [!!importId],
	});

	const {
		data: assetSystemFields,
		isLoading: isLoadingSystemFields,
		isFetching: isFetchingSystemFields,
	} = useQuery<Array<ISystemFields>>({
		queryKey: [ASSET_SYSTEM_FIELDS],
		queryFn: getSystemFields,
		...refetchOptions,
		enabled: [
			!!importId,
			(importType === EImportingDataType.asset),
			(assetOwnerOrganizationAdministrator
				|| networkOperatorOrganizationAdministrator
				|| assetOwnerAttributeUpdater
				|| assetConsumerOrganizationAdministrator
			),
		],
	});

	const {
		data: importTypes,
		isLoading: isLoadingImportTypes,
		isFetching: isFetchingImportTypes,
	} = useQuery({
			queryKey: [IMPORT_TYPES],
			queryFn: getImportTypes,
			select: (importTypes) => optionsFormatted(importTypes),
			enabled: [importType === EImportingDataType.asset],
			...refetchOptions,
		},
	);

	const {
		data: allFieldsData,
		isLoading: isLoadingAllFields,
		isFetching: isFetchingAllFields,
	} = useQuery<AxiosResponse, any>({
			queryKey: [ALL_FIELDS, importId],
			queryFn: extractQueryParams(getAllFields),
			select: (response) => {
				const fields = fieldsOptionsFormatted(response.data.allFields || []);

				return { data: fields, numRows: response.data.numRows, status: response.status };
			},
			enabled: [!!importId, importType === EImportingDataType.asset, isImportRulesComponent],
			refetchInterval: refetchIntervalAllFields,
			refetchOnWindowFocus: false,
			refetchOnmount: false,
			onSuccess: (data) => {
				if (checkIsRequestAccepted(data)) {

					setRefetchIntervalAllFields(false);
				}
			},
		},
	);

	return {
		allFields: allFieldsData?.data || [],
		numRows: allFieldsData?.numRows,
		assetFieldsSamples: assetFieldsSamples?.data || [],
		createAssetUploadUrl,
		downloadSample,
		executeAssetImport,
		assetImportStatistic: assetImportStatistic?.data || {},
		importTypes: importTypes || [],
		isLoadingAssetFieldsMapping: isLoadingSaveFieldsMapping
			|| (isLoadingFieldsSamples && isFetchingFieldsSamples) || !checkIsRequestAccepted(assetFieldsSamples),
		isLoadingAssetImportRules: isLoadingAssetImportRules || (isLoadingAllFields && isFetchingAllFields)
			|| allFieldsData?.status == ESuccessCodes.Accepted,
		isLoadingAssetResultStatistic: (isLoadingAssetsImportStatistic && isFetchingAssetsImportStatistic)
			|| isLoadingExecuteImport || !checkIsRequestAccepted(assetImportStatistic),
		isUploadingAssetFile: isLoadingCreateUploadUrl || (isLoadingSystemFields && isFetchingSystemFields)
			|| (isLoadingImportTypes && isFetchingImportTypes),
		sampleUrl: assetSampleUrl,
		saveAssetFieldsMapping,
		saveImportRules,
		assetSystemFields: assetSystemFields || [],
		uploadUrlData,
	};
};
